import { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import { useNavigate, useSearch, Link } from '@tanstack/react-router'
import { TopBarBackIconButton } from '../topbar/TopBarBackIconButton'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import { RosterCalendarView } from '../RosterCalendarView'
import { RosterListView } from './RosterListView'
import { RosterDateNav } from '../RosterDateNav'
import { RosterDutyStatusKey } from '../RosterKpiCard'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { ChevronDownIcon, ArrowLeft } from '../icons'
import { DesktopPageHeader } from '../desktop/DesktopPageHeader'
import { useIsDesktop } from '../ui/use-mobile'

type RosterViewFilter = 'me' | 'everyone'
import { ScheduledCrewDrawer } from '../ScheduledCrewDrawer'
import { AdminCrewRosterCalendarDrawer } from '../AdminCrewRosterCalendarDrawer'
import { useCurrentCrewMember } from '../../hooks/useCurrentCrewMember'
import { useCrewGetById } from '../../lib/hooks'
import { useRosterKpiSummary } from './useRosterKpiSummary'
import { AppLayout } from '../AppLayout'
import type { RosterEntryWithCrew } from '../RosterCalendarView'
import type { RosterFilters } from '../RosterFilterDrawer'

type TabValue = 'calendar' | 'list'

interface MyRosterSearchParams {
  tab?: TabValue
  selectedMonth?: string
}

interface MyRosterProps {
  crewId?: string
  tab?: TabValue
}

export function MyRoster({ crewId: crewIdProp, tab: tabProp }: MyRosterProps = {}) {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const search = useSearch({ strict: false }) as MyRosterSearchParams
  const { data: currentCrew } = useCurrentCrewMember()

  const isViewingOther = !!crewIdProp && crewIdProp !== currentCrew?.id

  // Tab state from URL or prop
  const activeTab: TabValue = (tabProp ?? search.tab) === 'list' ? 'list' : 'calendar'

  // Me/Everyone filter (only relevant when not viewing a specific crew member)
  const [viewFilter, setViewFilter] = useState<RosterViewFilter>('me')

  // Build filters based on viewFilter or crewId prop
  const filters: RosterFilters = useMemo(() => {
    if (isViewingOther) {
      return {
        crewMemberIds: [crewIdProp],
        dutyStatuses: [],
        aircraftIds: [],
        showAll: false,
      }
    }
    if (viewFilter === 'me' && currentCrew?.id) {
      return {
        crewMemberIds: [currentCrew.id],
        dutyStatuses: [],
        aircraftIds: [],
        showAll: false,
      }
    }
    return {
      crewMemberIds: [],
      dutyStatuses: [],
      aircraftIds: [],
      showAll: true,
    }
  }, [isViewingOther, crewIdProp, viewFilter, currentCrew?.id])

  // KPI summary: distinct crew per status for the selected month, company-wide
  // (independent of the viewed crew / Me-Everyone filter), matching CrewRoster.
  const summary = useRosterKpiSummary({ selectedMonth: search.selectedMonth })
  const hasSummaryData = Object.values(summary).some((count) => count > 0)

  // Shared date nav: displayed month derives from the URL, defaulting to today.
  const displayMonth = search.selectedMonth
    ? dayjs(search.selectedMonth, 'YYYY-MM')
    : dayjs()
  const goToMonth = (month: dayjs.Dayjs) => {
    navigate({
      search: { ...search, selectedMonth: month.format('YYYY-MM') } as any,
      replace: true,
    })
  }

  // Fetch crew member name when viewing another crew member
  const { data: viewedCrew } = useCrewGetById(isViewingOther ? crewIdProp : null)
  const displayTitle = isViewingOther
    ? (viewedCrew?.name ? `${viewedCrew.name}'s Roster` : 'Roster')
    : 'My Roster'

  // Calendar day click -> scheduled crew drawer
  const [showScheduledCrewDrawer, setShowScheduledCrewDrawer] = useState(false)
  const [selectedDateForDrawer, setSelectedDateForDrawer] = useState<Date | null>(null)
  const [selectedEntriesForDrawer, setSelectedEntriesForDrawer] = useState<RosterEntryWithCrew[]>([])


  const handleTabChange = (value: string) => {
    navigate({
      search: { ...search, tab: value } as any,
      replace: true,
    })
  }

  const handleCalendarDayClick = (date: Date, entries: RosterEntryWithCrew[]) => {
    setSelectedDateForDrawer(date)
    setSelectedEntriesForDrawer(entries)
    setShowScheduledCrewDrawer(true)
  }



  const mode = viewFilter === 'me' ? 'me' as const : 'team' as const

  // Me / Everyone filter — reused as the mobile topbar's right element and the
  // desktop page header's action slot.
  const viewFilterDropdown = !isViewingOther ? (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-content-primary-bold outline-none">
        {viewFilter === 'me' ? 'Me' : 'Everyone'}
        <ChevronDownIcon className="size-4 opacity-50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setViewFilter('me')}>Me</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setViewFilter('everyone')}>Everyone</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined

  return (
    <AppLayout
      footer={
        hasSummaryData ? (
          <div className="px-gutter pb-2">
            <RosterDutyStatusKey summary={summary} />
          </div>
        ) : undefined
      }
      topBar={
        isDesktop ? (
          <div className="px-gutter pt-8 pb-2">
            <DesktopPageHeader
              title={displayTitle}
              breadcrumb={
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate({ to: '/home' })}
                    className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
                    aria-label="Back to home"
                  >
                    <ArrowLeft className="size-4" />
                  </button>
                  <span>
                    <Link to="/home" className="hover:underline">
                      Home
                    </Link>
                    <span className="text-content-primary-subtle"> / {displayTitle}</span>
                  </span>
                </span>
              }
              action={viewFilterDropdown}
            />
          </div>
        ) : (
          <TopBarBackIconButton
            title={displayTitle}
            rightElement={viewFilterDropdown}
          />
        )
      }
    >
      {/* Date navigation — drives selectedMonth for both views */}
      <RosterDateNav
        month={displayMonth}
        onMonthChange={goToMonth}
        className="pb-2"
      />

      {/* Tabs */}
      <div className="pb-2">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="w-full">
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="list">List</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 relative w-full z-10 flex-1 min-h-0">
        {activeTab === 'calendar' && (
          <RosterCalendarView
            onDayClick={handleCalendarDayClick}
            filters={filters}
            mode={mode}
          />
        )}

        {activeTab === 'list' && (
          <RosterListView
            filters={filters}
            mode={mode}
            className="flex-1 min-h-0"
          />
        )}
      </div>

      {/* Day click drawer: admin-style for "Everyone", roster entry for "Me" */}
      {viewFilter === 'me' ? (
        <ScheduledCrewDrawer
          isOpen={showScheduledCrewDrawer}
          onOpenChange={setShowScheduledCrewDrawer}
          date={selectedDateForDrawer}
          entries={selectedEntriesForDrawer}
          aircraftIds={filters.aircraftIds}
        />
      ) : (
        <AdminCrewRosterCalendarDrawer
          isOpen={showScheduledCrewDrawer}
          onOpenChange={setShowScheduledCrewDrawer}
          date={selectedDateForDrawer}
          entries={selectedEntriesForDrawer as any}
          aircraftIds={filters.aircraftIds}
        />
      )}

    </AppLayout>
  )
}
